/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Search, User, PlusCircle, Leaf, History, ArrowUpRight, Users, Info, UserCircle, KeyRound, LogOut, Home, Menu, X, FileText, HelpCircle, Sprout } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AdModal from './AdModal';
import ProfileModal from './ProfileModal';
import HouseholdModal from './HouseholdModal';
import ChangePasswordModal from './ChangePasswordModal';
import { useAuth } from './AuthContext';

const NAV_LINKS = [
  { to: '/ponuda', label: 'Ponuda', icon: Leaf },
  { to: '/prodavci', label: 'Prodavci', icon: Users },
  { to: '/o-nama', label: 'O nama', icon: Info },
];

const MOBILE_EXTRA_LINKS = [
  { to: '/kako-prodati', label: 'Kako prodati?', icon: Sprout },
  { to: '/cesta-pitanja', label: 'Česta pitanja', icon: HelpCircle },
];

export default function Header({ searchQuery, setSearchQuery, onOpenAuth }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoggedIn, logout } = useAuth();
  const isActive = (path) => location.pathname === path || (path === '/ponuda' && location.pathname === '/');
  const [isAdModalOpen, setIsAdModalOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHouseholdOpen, setIsHouseholdOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const saved = localStorage.getItem('recentSearches');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileSearchInputRef = useRef(null);

  const initials = (() => {
    const source = user?.name || user?.ime || user?.email || '';
    if (!source) return '';
    if (source.includes('@')) return source[0].toUpperCase();
    return source
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  })();

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/ponuda');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
        setIsMobileSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Zatvori mobilne panele pri promeni rute.
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileSearchOpen) {
      requestAnimationFrame(() => mobileSearchInputRef.current?.focus());
    }
  }, [isMobileSearchOpen]);

  const handleSearchSubmit = (query) => {
    setSearchQuery(query);
    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);
    const trimmed = query.trim();
    if (trimmed && !recentSearches.includes(trimmed)) {
      setRecentSearches((prev) => {
        const next = [trimmed, ...prev].slice(0, 5);
        try { localStorage.setItem('recentSearches', JSON.stringify(next)); } catch {}
        return next;
      });
    }
    // Pretraga radi samo na /ponuda — sa drugih strana vodi korisnika tamo.
    if (location.pathname !== '/ponuda') {
      navigate(trimmed ? `/ponuda?q=${encodeURIComponent(trimmed)}` : '/ponuda');
    }
  };

  const handlePostAd = () => {
    setIsMobileMenuOpen(false);
    if (isLoggedIn) {
      setIsAdModalOpen(true);
    } else {
      onOpenAuth();
    }
  };

  const RecentSearchesList = ({ className = '' }) => (
    <div className={className}>
      <div className="flex items-center gap-2 text-sage-400 text-xs font-bold uppercase tracking-wider mb-3 px-2">
        <History size={14} />
        Nedavne pretrage
      </div>
      {recentSearches.length > 0 ? (
        <div className="space-y-1">
          {recentSearches.map((item, idx) => (
            <button
              key={idx}
              onClick={() => handleSearchSubmit(item)}
              className="w-full text-left px-3 py-2 rounded-xl hover:bg-sage-50 text-earth-900 text-sm transition-colors flex items-center justify-between group"
            >
              {item}
              <ArrowUpRight size={14} className="text-sage-300 group-hover:text-sage-600 transition-colors" />
            </button>
          ))}
        </div>
      ) : (
        <p className="px-3 py-6 text-center text-sm text-sage-400 italic">
          Nemate nedavnih pretraga.
        </p>
      )}
    </div>
  );

  return (
    <>
      <nav ref={mobileMenuRef} className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-sage-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 gap-2">
            <div className="flex items-center min-w-0">
              <Link
                to="/ponuda"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setSearchQuery('');
                }}
                className="flex items-center gap-2 cursor-pointer lg:mr-12"
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2"
                >
                  <div className="w-10 h-10 bg-sage-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    <Leaf size={24} />
                  </div>
                  <span className="text-xl sm:text-2xl font-serif font-bold text-sage-800 tracking-tight whitespace-nowrap">Eko Pijaca</span>
                </motion.div>
              </Link>

              <div className="hidden lg:flex items-center gap-8">
                {NAV_LINKS.map(({ to, label, icon: Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    className={`text-sm font-bold transition-all flex items-center gap-2 py-2 px-1 border-b-2 ${
                      isActive(to)
                        ? 'text-sage-600 border-sage-600'
                        : 'text-sage-400 border-transparent hover:text-sage-600'
                    }`}
                  >
                    <Icon size={16} />
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="hidden md:flex flex-1 max-w-md mx-8 relative" ref={dropdownRef}>
              <div className="relative w-full group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-600 group-focus-within:scale-110 transition-transform" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onFocus={() => setIsDropdownOpen(true)}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(searchQuery)}
                  placeholder="Pretraži poljoprivredne proizvode..."
                  className="w-full bg-sage-50 border-none rounded-2xl py-2.5 pl-10 pr-4 focus:ring-2 focus:ring-sage-200 text-earth-900 placeholder-sage-600/50 transition-all outline-none"
                />
              </div>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-white rounded-3xl shadow-2xl border border-sage-100 overflow-hidden z-[100]"
                  >
                    <RecentSearchesList className="p-4" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <button
                onClick={() => setIsMobileSearchOpen((o) => !o)}
                aria-label="Pretraga"
                className="md:hidden p-2.5 text-sage-700 hover:bg-sage-50 rounded-xl transition-colors"
              >
                <Search size={22} />
              </button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePostAd}
                aria-label="Postavi oglas"
                className="bg-sage-600 text-white px-3 sm:px-5 py-2.5 rounded-2xl flex items-center gap-2 font-medium shadow-sm hover:bg-sage-700 transition-colors"
              >
                <PlusCircle size={20} />
                <span className="hidden sm:inline">Postavi oglas</span>
              </motion.button>

              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsUserMenuOpen((o) => !o)}
                    className="w-11 h-11 rounded-2xl bg-sage-100 text-sage-800 font-bold flex items-center justify-center hover:bg-sage-600 hover:text-white transition-colors shadow-sm"
                    aria-label="Korisnički meni"
                  >
                    {initials || <User size={20} />}
                  </motion.button>

                  <AnimatePresence>
                    {isUserMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute right-0 top-full mt-2 w-60 bg-white rounded-2xl shadow-2xl border border-sage-100 overflow-hidden z-[100] py-2"
                      >
                        {(user.name || user.ime || user.email) && (
                          <div className="px-4 py-3 border-b border-sage-50">
                            <p className="text-sm font-bold text-earth-900 truncate">
                              {user.name || user.ime || user.email}
                            </p>
                            {user.email && (user.name || user.ime) && (
                              <p className="text-xs text-sage-500 truncate">{user.email}</p>
                            )}
                          </div>
                        )}

                        <button
                          onClick={() => { setIsUserMenuOpen(false); navigate('/moji-oglasi'); }}
                          className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-earth-900 hover:bg-sage-50 transition-colors"
                        >
                          <FileText size={18} className="text-sage-600" />
                          Moji Oglasi
                        </button>
                        <button
                          onClick={() => { setIsUserMenuOpen(false); setIsProfileOpen(true); }}
                          className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-earth-900 hover:bg-sage-50 transition-colors"
                        >
                          <UserCircle size={18} className="text-sage-600" />
                          Moj Profil
                        </button>
                        <button
                          onClick={() => { setIsUserMenuOpen(false); setIsHouseholdOpen(true); }}
                          className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-earth-900 hover:bg-sage-50 transition-colors"
                        >
                          <Home size={18} className="text-sage-600" />
                          Moje Domaćinstvo
                        </button>
                        <button
                          onClick={() => { setIsUserMenuOpen(false); setIsChangePasswordOpen(true); }}
                          className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-earth-900 hover:bg-sage-50 transition-colors"
                        >
                          <KeyRound size={18} className="text-sage-600" />
                          Izmeni Šifru
                        </button>
                        <div className="border-t border-sage-50 my-1" />
                        <button
                          onClick={handleLogout}
                          className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut size={18} />
                          Odjavi se
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <button
                  onClick={onOpenAuth}
                  aria-label="Prijava"
                  className="p-2 text-sage-700 hover:bg-sage-50 rounded-xl transition-colors"
                >
                  <User size={24} />
                </button>
              )}

              <button
                onClick={() => setIsMobileMenuOpen((o) => !o)}
                aria-label={isMobileMenuOpen ? 'Zatvori meni' : 'Otvori meni'}
                aria-expanded={isMobileMenuOpen}
                className="lg:hidden p-2.5 text-sage-700 hover:bg-sage-50 rounded-xl transition-colors"
              >
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobilna pretraga — panel ispod glavne trake */}
        <AnimatePresence>
          {isMobileSearchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden border-t border-sage-100 bg-white"
            >
              <div className="px-4 py-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-600" size={18} />
                  <input
                    ref={mobileSearchInputRef}
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(searchQuery)}
                    placeholder="Pretraži proizvode..."
                    className="w-full bg-sage-50 border-none rounded-2xl py-3 pl-10 pr-4 focus:ring-2 focus:ring-sage-200 text-earth-900 placeholder-sage-600/50 transition-all outline-none"
                  />
                </div>
                <RecentSearchesList className="pt-4" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobilni meni */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden overflow-hidden border-t border-sage-100 bg-white"
            >
              <div className="px-4 py-3 space-y-1">
                {[...NAV_LINKS, ...MOBILE_EXTRA_LINKS].map(({ to, label, icon: Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-colors ${
                      isActive(to)
                        ? 'bg-sage-50 text-sage-700'
                        : 'text-sage-600 hover:bg-sage-50'
                    }`}
                  >
                    <Icon size={18} className={isActive(to) ? 'text-sage-600' : 'text-sage-400'} />
                    {label}
                  </Link>
                ))}
                {isLoggedIn && (
                  <>
                    <div className="border-t border-sage-50 my-2" />
                    <Link
                      to="/moji-oglasi"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-colors ${
                        isActive('/moji-oglasi')
                          ? 'bg-sage-50 text-sage-700'
                          : 'text-sage-600 hover:bg-sage-50'
                      }`}
                    >
                      <FileText size={18} className="text-sage-400" />
                      Moji Oglasi
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <AdModal isOpen={isAdModalOpen} onClose={() => setIsAdModalOpen(false)} />
      <ProfileModal isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} />
      <HouseholdModal isOpen={isHouseholdOpen} onClose={() => setIsHouseholdOpen(false)} />
      <ChangePasswordModal isOpen={isChangePasswordOpen} onClose={() => setIsChangePasswordOpen(false)} />
    </>
  );
}
